import {
  Connection,
  LAMPORTS_PER_SOL,
  PublicKey,
  SystemProgram,
  Transaction,
  TransactionInstruction,
} from '@solana/web3.js';
import {
  TOKEN_PROGRAM_ID,
  TOKEN_2022_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  getAssociatedTokenAddressSync,
  createAssociatedTokenAccountInstruction,
  createTransferCheckedInstruction,
} from '@solana/spl-token';
import { ed25519SignWithScalar } from './localSign';

export type Network = 'mainnet' | 'devnet';

// Defaults: VITE_SOLANA_*_RPC_URL injected at build time (the Para-hosted deploy
// sets these to a Para-owned Alchemy app, with Allowed Origins locked to the
// connect.getpara.com domains in Alchemy's dashboard so the embedded key is
// useless if extracted). When unset (e.g. for someone who clones the repo and
// runs `yarn dev`), fall back to public endpoints. Users can also override at
// runtime via the Custom RPC field.
const RPC: Record<Network, string> = {
  mainnet: import.meta.env.VITE_SOLANA_MAINNET_RPC_URL || 'https://solana-rpc.publicnode.com',
  devnet: import.meta.env.VITE_SOLANA_DEVNET_RPC_URL || 'https://api.devnet.solana.com',
};

export function rpcUrl(net: Network, override?: string): string {
  const o = override?.trim();
  return o || RPC[net];
}

export function explorerAccountUrl(address: string, net: Network): string {
  const cluster = net === 'mainnet' ? '' : '?cluster=devnet';
  return `https://solscan.io/account/${address}${cluster}`;
}

export function explorerTxUrl(sig: string, net: Network): string {
  const cluster = net === 'mainnet' ? '' : '?cluster=devnet';
  return `https://solscan.io/tx/${sig}${cluster}`;
}

export function explorerTokensUrl(address: string, net: Network): string {
  const cluster = net === 'mainnet' ? '' : '?cluster=devnet';
  return `https://solscan.io/account/${address}${cluster}#tokenAccounts`;
}

export function explorerHistoryUrl(address: string, net: Network): string {
  const cluster = net === 'mainnet' ? '' : '?cluster=devnet';
  return `https://solscan.io/account/${address}${cluster}#transactions`;
}

export interface TokenHolding {
  mint: string;
  amount: bigint;
  decimals: number;
  uiAmount: string;
  programId: string;
}

export async function getSolBalance(connection: Connection, owner: PublicKey): Promise<number> {
  const lamports = await connection.getBalance(owner, 'confirmed');
  return lamports / LAMPORTS_PER_SOL;
}

export async function getTokenHoldings(connection: Connection, owner: PublicKey): Promise<TokenHolding[]> {
  const programs = [TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID];
  const all: TokenHolding[] = [];
  for (const programId of programs) {
    const resp = await connection.getParsedTokenAccountsByOwner(owner, { programId });
    for (const { account } of resp.value) {
      const info = (account.data as { parsed: { info: { mint: string; tokenAmount: { amount: string; decimals: number; uiAmountString: string } } } }).parsed.info;
      const amount = BigInt(info.tokenAmount.amount);
      if (amount === 0n) continue;
      all.push({
        mint: info.mint,
        amount,
        decimals: info.tokenAmount.decimals,
        uiAmount: info.tokenAmount.uiAmountString,
        programId: programId.toBase58(),
      });
    }
  }
  return all.sort((a, b) => a.mint.localeCompare(b.mint));
}

async function signAndSubmit(
  connection: Connection,
  scalar: Uint8Array,
  pubkey: Uint8Array,
  instructions: TransactionInstruction[],
): Promise<string> {
  const owner = new PublicKey(pubkey);
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
  const tx = new Transaction({ feePayer: owner, blockhash, lastValidBlockHeight });
  for (const ix of instructions) tx.add(ix);

  const message = tx.serializeMessage();
  const signature = ed25519SignWithScalar(scalar, pubkey, message);
  tx.addSignature(owner, Buffer.from(signature));

  if (!tx.verifySignatures()) {
    throw new Error('signature verification failed before submit');
  }

  const txSig = await connection.sendRawTransaction(tx.serialize(), {
    skipPreflight: false,
    preflightCommitment: 'confirmed',
  });
  await waitForConfirmation(connection, txSig, lastValidBlockHeight);
  return txSig;
}

// HTTP-only confirmation poll. We avoid `connection.confirmTransaction` because
// it opens a `signatureSubscribe` WebSocket, and the default browser-friendly
// mainnet RPC (publicnode) doesn't support pub/sub — web3.js spam-retries and
// eventually fails, masking a tx that actually broadcast.
async function waitForConfirmation(
  connection: Connection,
  signature: string,
  lastValidBlockHeight: number,
  timeoutMs = 60_000,
  pollMs = 2_000,
): Promise<void> {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    const { value } = await connection.getSignatureStatus(signature, { searchTransactionHistory: false });
    if (value) {
      if (value.err) throw new Error(`transaction failed: ${JSON.stringify(value.err)}`);
      if (value.confirmationStatus === 'confirmed' || value.confirmationStatus === 'finalized') return;
    }
    const currentHeight = await connection.getBlockHeight('confirmed');
    if (currentHeight > lastValidBlockHeight) {
      throw new Error('blockhash expired before confirmation');
    }
    await new Promise(r => setTimeout(r, pollMs));
  }
  throw new Error('timed out waiting for confirmation');
}

export async function sendSolTransfer(
  connection: Connection,
  scalar: Uint8Array,
  pubkey: Uint8Array,
  recipient: string,
  amountSol: number,
): Promise<string> {
  const lamports = Math.round(amountSol * LAMPORTS_PER_SOL);
  if (!Number.isFinite(lamports) || lamports <= 0) {
    throw new Error('amount must be a positive number of SOL');
  }
  const ix = SystemProgram.transfer({
    fromPubkey: new PublicKey(pubkey),
    toPubkey: new PublicKey(recipient),
    lamports,
  });
  return signAndSubmit(connection, scalar, pubkey, [ix]);
}

export async function sendSplTransfer(
  connection: Connection,
  scalar: Uint8Array,
  pubkey: Uint8Array,
  holding: TokenHolding,
  recipient: string,
  uiAmount: number,
): Promise<string> {
  const owner = new PublicKey(pubkey);
  const mint = new PublicKey(holding.mint);
  const programId = new PublicKey(holding.programId);
  const recipientOwner = new PublicKey(recipient);

  const sourceAta = getAssociatedTokenAddressSync(mint, owner, true, programId, ASSOCIATED_TOKEN_PROGRAM_ID);
  const destAta = getAssociatedTokenAddressSync(mint, recipientOwner, true, programId, ASSOCIATED_TOKEN_PROGRAM_ID);

  const rawAmount = BigInt(Math.round(uiAmount * Math.pow(10, holding.decimals)));
  if (rawAmount <= 0n) throw new Error('amount must be positive');
  if (rawAmount > holding.amount) throw new Error(`amount exceeds balance (${holding.uiAmount})`);

  const ixs: TransactionInstruction[] = [];

  const destInfo = await connection.getAccountInfo(destAta, 'confirmed');
  if (!destInfo) {
    ixs.push(
      createAssociatedTokenAccountInstruction(owner, destAta, recipientOwner, mint, programId, ASSOCIATED_TOKEN_PROGRAM_ID),
    );
  }

  ixs.push(
    createTransferCheckedInstruction(sourceAta, mint, destAta, owner, rawAmount, holding.decimals, [], programId),
  );

  return signAndSubmit(connection, scalar, pubkey, ixs);
}
